import {EventEmitter, Injectable} from '@angular/core';
import {AuthService} from './api/auth.service';
import {catchError, EMPTY, filter, Observable, tap} from 'rxjs';
import {MemberModel} from '../models/user.model';
import {TokenService} from './token.service';
import {TranslateService} from '@ngx-translate/core';
import {ModalService} from './modal/modal.service';
import {Router} from '@angular/router';
import {MenuModel, SubMenuModel} from '../models/menu.model';
import { DialogsMTService } from './modal/dialogs-mt.service';

@Injectable({
    providedIn: 'root',
})
export class MemberService {
    public subMenuIcons = {
        PROJECT_CONTRACTS: 'bi bi-ui-checks',
        MY_CONTRACTS: 'bi bi-ui-radios',
        MY_CONTRACTS_MT: 'bi bi-ui-radios-grid',
        MY_TASKS_MT: 'bi bi-card-list',
        PATTERNS: 'bi bi-file-earmark-font',
        INCOMINGS_contracts: 'bi bi-inboxes',
        ON_EXECUTION: 'bi bi-pencil',
        COMPLITED: 'bi bi-check2-all',
        ARCHIEVED: 'bi bi-archive',
        BOARDS: 'bi bi-table',
        MY_TASKS: 'bi bi-card-list',
        SETTINGS: 'bi bi-gear',
        LINKS_BETWEEN_BOARDS: 'bi bi-diagram-3',
        DOCUMENTS: 'bi bi-file-earmark-word',
        INCOMING_DOCS: 'bi bi-inboxes',
        DOCUMENTS_APPROVAL: 'bi bi-file-earmark-check',
        SIGNED_DOCS: 'bi bi-check2-all',
        DOCS_REGISTRY: 'bi bi-briefcase',
        CUSTOM_FIELDS: 'bi bi-gear',
        PRODUCTS: 'bi bi-box',
        STORAGE: 'bi bi-diagram-3',
        DEMAND: 'bi bi-bar-chart',
        SUPPLY: 'bi bi-boxes',
        ACCOUNT: 'bi bi-wallet',
        CREDITS: 'bi bi-credit-card',
        FACTORING: 'bi bi-cash-coin',
        BANK_GUARANTEE: 'bi bi-safe2',
        MY_WALLETS: 'bi bi-safe2',
        EMPLOYEES_users: 'bi bi-person-lines-fill',
        DEPARTMENTS: 'bi bi-collection',
        CONTRACTORS: 'bi bi-buildings',
        EMPLOYEES_statistics: 'bi bi-people',
        TASKS_statistics: 'bi bi-list-task',
        FINANCES_statistics: 'bi bi-cash-coin',
        CONTRACTS_statistics: 'bi bi-calendar4-week',
        ASSIGNMENTS: 'bi bi-list-task',
    };
    public refreshMenuEvent: EventEmitter<MenuModel[]> = new EventEmitter<MenuModel[]>();
    protected member: MemberModel;
    protected lastMemberRequestDate: Date;
    protected lastMemberRequestPromise: Promise<MemberModel>;
    protected membersAll: MemberModel[] = [];
    protected lastAllMembersRequestDate: Date;
    protected lastAllMembersRequestPromise: Promise<MemberModel[]>;

    constructor(
        protected authService: AuthService,
        protected tokenService: TokenService,
        protected translateService: TranslateService,
        protected modalService: ModalService,
        protected dialogsMTService: DialogsMTService,
        protected router: Router,
    ) {
    }

    public getCurrentMember(reload = false): Promise<MemberModel> {
        if (this.isReloadData(this.lastMemberRequestDate)) {
            reload = true;
        }
        if (!reload && this.lastMemberRequestPromise) {
            return this.lastMemberRequestPromise;
        }

        if (reload || !this.lastMemberRequestPromise) {
            this.lastMemberRequestDate = new Date();
            this.lastMemberRequestPromise = new Promise(resolve => {
                if (this.tokenService.getToken()) {
                    if (!reload && this.member) {
                        resolve(this.member);
                    } else {
                        this.getMemberGuid().subscribe((res: MemberModel) => {
                            this.member = res;
                            resolve(this.member);
                        });
                    }
                } else {
                    resolve(null);
                }
                this.lastMemberRequestPromise = null;
            });
        }
        return this.lastMemberRequestPromise;
    }

    public getAllMembers(reload = false): Promise<MemberModel[]> {
        if (this.isReloadData(this.lastAllMembersRequestDate)) {
            reload = true;
        }
        if (!reload && this.lastAllMembersRequestPromise) {
            return this.lastAllMembersRequestPromise;
        }

        if (reload || !this.lastAllMembersRequestPromise) {
            this.lastAllMembersRequestDate = new Date();
            this.lastAllMembersRequestPromise = new Promise(resolve => {
                if (!reload && this.membersAll.length) {
                    resolve(this.membersAll);
                } else {
                    this.authService.getAllUsers('').subscribe((res: { users: MemberModel[] }) => {
                        this.membersAll = res.users;
                        resolve(this.membersAll);
                    });
                }
                this.lastAllMembersRequestPromise = null;
            });
        }
        return this.lastAllMembersRequestPromise;
    }

    public getMemberGuid(guid:string|null = null): Observable<MemberModel> {
        return this.authService.getUserByGUID(guid);
    }

    public async getMembersByGuids(guid: string[]): Promise<MemberModel[]> {
        return new Promise((resolve, reject) => {
            this.authService.getAllUsers(guid.toString()).subscribe({
                next: res => {
                    resolve(res.users);
                }, error: err => {
                    reject(err);
                }
            });
        });
    }

    public async memberGuidsHandler(guids: string[]): Promise<MemberModel[]> {
        return new Promise(async resolve => {
            const savedMembers = JSON.parse(localStorage.getItem('savedMembers')) ?? {};
            const guidsForRequest: string[] = [];

            const requestedMembers: MemberModel[] = [];
            const currentTime = new Date().getTime();

            guids.forEach((guid: string) => {
                if (savedMembers && savedMembers[guid] && currentTime - savedMembers[guid].requestTime < 600000) {
                    requestedMembers.push(savedMembers[guid]);
                    return;
                }

                if (!guidsForRequest.includes(guid)) {
                    guidsForRequest.push(guid);
                }
            });

            if (guidsForRequest.length > 0) {
                await this.getMembersByGuids(guidsForRequest).then(res => {
                    res.forEach(member => {
                        member.requestTime = currentTime;
                        savedMembers[member.guid] = member;
                        requestedMembers.push(member);
                    });
                    localStorage.setItem('savedMembers', JSON.stringify(savedMembers));
                }).catch(() => {
                });
            }

            resolve(requestedMembers);
        });
    }

    public logout(): void {
      this.dialogsMTService.confirmDialog({actionHeader: 'CONFIRM.HEADERS.ATTENTION',actionBody: 'CONFIRM.HEADERS.LOGOUT'})
      .pipe(
        filter(res => res.isConfirmed),
        tap(()=>{
          this.tokenService.clearToken();
          const snap = this.router.routerState.snapshot;
          this.router.navigate(['/login'], {queryParams: {lastpage: snap.url}});
        }),
        catchError(e=>{
          return EMPTY
        }))
      .subscribe()
/*         const modalSettings: any = {
            modalHeader: this.translateService.instant('CONFIRM.HEADERS.LOGOUT'),
            buttonSubmit: this.translateService.instant('CONFIRM.BUTTONS.LOGOUT'),
            buttonClose: this.translateService.instant('CONFIRM.BUTTONS.CANCEL'),
        };

        const modal = this.modalService.createFromSettings(modalSettings);
        modal.result.then(async res => {
            if (res) {
                this.tokenService.clearToken();
                const snap = this.router.routerState.snapshot;
                await this.router.navigate(['/login'], {queryParams: {lastpage: snap.url}});
            }
        }).catch(() => {
            return;
        }); */
    }

    getMenu(isAll=false): Observable<{ project_settings: MenuModel[], submenu_items: SubMenuModel[] }> {
        return this.authService.getMenu(isAll);
    }

    saveMenu(data: string): Observable<{ project_settings: MenuModel[], submenu_items: SubMenuModel[] }> {
        return this.authService.saveMenu(data);
    }

    protected isReloadData(lastRequest: Date): boolean {
        if (!lastRequest) {
            return true;
        }
        const date = new Date();
        const dl = date.getTime() - lastRequest.getTime();

        return dl > 60 * 5 * 1000;
    }

    getAvatar(min: boolean, guid: string): Observable<any> {
        return this.authService.getAvatar(min, guid);
    }
}
